// filepath: apps/web/src/features/cabinet/TenantShell.tsx
//
// FR-UI-CABINET-1 AC-1 — the persistent tenant workspace shell.
//
// Layout wrapper rendered AROUND every authenticated section's
// content inside `AuthStateRouter`. Owns:
//   - the persistent left-nav `<Sidebar />` (an `<aside>` rail);
//   - the `<main data-testid="app-root">` landmark (FR-UI-CORE-9);
//   - the visually-hidden `<h1>` page heading that labels the
//     landmark for screen readers.
//
// PERSISTENCE (AC-1): because `AuthStateRouter` renders
// `<TenantShell>{content}</TenantShell>` at the SAME tree position
// for every authenticated section (via the `authed()` helper in
// `App.tsx`), React reconciles `<TenantShell>` as a stable element
// across section switches — the sidebar DOM and state survive, only
// the children (the matched section's content) swap. This is what
// makes the sidebar "persistent" without `react-router-dom` (AC-9).
//
// BOOTSTRAP CHAIN: the shell mounts INSIDE the existing
// `QueryClientProvider → TenantProvider → AppBootstrap → AuthProvider
// → SilentRefreshInstaller → AuthStateRouter` tree (AC-9 — zero
// react-router-dom). It does NOT replace or reorder any of it.
//
// INV-2: the shell renders only manifest-entitled nav (via
// `<Sidebar />` / `useNavManifest`). It never authorizes an action.
// INV-9: all chrome via `t(key, fallback)` text nodes.
//
// NOTE (a11y): reused-unchanged section components
// (`TaskInboxPage`, `ObservabilityDashboardPage`) render their own
// `<main>` landmark for their standalone-page heritage. Wrapped
// inside this shell's `<main>`, they produce a nested `<main>`.
// This is a noted a11y limitation (landmark-no-duplicate-main,
// moderate — not serious/critical, so it does not fail the axe-core
// gate per FRONTEND_WEB_DEVELOPMENT_GUIDE §12.1). The follow-up to
// refactor those reused sections to `<section>` is out of scope for
// this FR (they are REUSED-UNCHANGED by contract).

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { AppChrome } from "@/chrome/AppChrome";
import { Sidebar } from "./Sidebar";
import { Breadcrumbs } from "./chrome";
import { SPACE, SIDEBAR_WIDTH, VISUALLY_HIDDEN } from "./styles";

export interface TenantShellProps {
  /** The matched section's content (rendered inside the `<main>`). */
  children: React.ReactNode;
}

/**
 * TenantShell — the persistent sidebar + content layout.
 *
 * Composed by `App.tsx`'s `authed()` helper around every
 * authenticated section. The sidebar persists across section
 * switches (see module header); only `children` swap.
 *
 * FR-UI-CABINET-2 + FR-UI-NFR-4: the layout wraps everything in
 * `AppChrome variant="tenant"` (the shared chrome primitive). The
 * sidebar + `<main>` landmark are owned by AppChrome; this shell
 * supplies the section breadcrumbs and the visually-hidden page
 * heading that labels the AppChrome main landmark. We DO NOT wrap
 * children in another `<main>` because AppChrome already provides
 * one — that would produce nested `<main>` landmarks (a11y
 * violation per landmark-no-duplicate-main).
 */
export function TenantShell({
  children,
}: TenantShellProps): React.ReactElement {
  const { t } = useTranslation();
  return (
    <AppChrome
      variant="tenant"
      sidebar={<Sidebar />}
      contentAriaLabelledBy="cabinet-shell-title"
    >
      <h1 id="cabinet-shell-title" style={VISUALLY_HIDDEN}>
        {t("cabinet.title", "Tenant workspace")}
      </h1>
      <Breadcrumbs />
      {children}
    </AppChrome>
  );
}

/**
 * The width the content slot is offset by (for any descendant that
 * needs to know the sidebar rail width). Kept in sync with
 * `SIDEBAR_WIDTH` in `styles.ts`.
 */
export const SHELL_SIDEBAR_WIDTH = SIDEBAR_WIDTH;

/** Re-export so callers can compute content-area widths if needed. */
export { SPACE as SHELL_SPACE };
